import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';

interface Order {
  id: string;
  order_number: string;
  status: string;
  full_name: string;
  email: string;
  phone: string;
  emirate: string;
  area: string;
  building: string;
  apartment: string;
  delivery_type: string;
  payment_method: string;
  subtotal: number;
  delivery_fee: number;
  total: number;
  estimated_delivery: string;
  created_at: string;
  notes: string;
}

const STEPS = [
  { label: 'Order Placed', icon: 'ri-shopping-bag-3-line', status: 'pending' },
  { label: 'Processing', icon: 'ri-settings-3-line', status: 'processing' },
  { label: 'Shipped', icon: 'ri-truck-line', status: 'shipped' },
  { label: 'Delivered', icon: 'ri-checkbox-circle-line', status: 'delivered' },
];

const STATUS_STEP_MAP: Record<string, number> = {
  pending: 0,
  processing: 1,
  shipped: 2,
  delivered: 3,
};

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatPayment(method: string) {
  if (!method) return '—';
  if (method === 'cod' || method === 'cash_on_delivery') return 'Cash on Delivery';
  return method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ProgressTracker({ status }: { status: string }) {
  const activeStep = STATUS_STEP_MAP[status] ?? 0;

  return (
    <div className="relative flex items-start justify-between w-full px-2 md:px-6">
      {/* Connecting lines */}
      <div className="absolute top-6 left-0 right-0 flex items-center px-10 md:px-16 pointer-events-none" style={{ zIndex: 0 }}>
        {STEPS.slice(0, -1).map((_, i) => {
          const filled = i < activeStep;
          return (
            <div
              key={i}
              className="flex-1 h-1 rounded-full mx-1"
              style={{
                background: filled
                  ? 'linear-gradient(90deg, #f97316, #ec4899)'
                  : '#e5e7eb',
                transition: 'background 0.4s ease',
              }}
            />
          );
        })}
      </div>

      {STEPS.map((step, i) => {
        const isCompleted = i < activeStep;
        const isCurrent = i === activeStep;
        const isFuture = i > activeStep;

        return (
          <div key={i} className="flex flex-col items-center gap-2 flex-1 relative z-10">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                isCurrent ? 'ring-4 ring-orange-300 ring-offset-2 animate-pulse' : ''
              }`}
              style={{
                background: isCompleted
                  ? 'linear-gradient(135deg, #f97316, #ec4899)'
                  : isCurrent
                  ? '#fff7ed'
                  : '#f3f4f6',
                border: isCurrent ? '2px solid #f97316' : 'none',
              }}
            >
              <i
                className={`${step.icon} text-xl`}
                style={{
                  color: isCompleted ? '#fff' : isCurrent ? '#f97316' : '#9ca3af',
                }}
              />
            </div>
            <span
              className={`text-xs font-semibold text-center leading-tight ${
                isCompleted
                  ? 'text-orange-500'
                  : isCurrent
                  ? 'text-gray-800'
                  : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
            {isCurrent && (
              <span className="text-xs text-orange-500 font-medium bg-orange-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                Current
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FeatureCards() {
  const cards = [
    {
      icon: 'ri-shield-check-line',
      title: 'Secure Tracking',
      desc: 'Your order info is private and secure',
      color: 'from-orange-500 to-pink-600',
    },
    {
      icon: 'ri-truck-line',
      title: 'Real-time Updates',
      desc: 'Track your delivery status instantly',
      color: 'from-pink-500 to-orange-400',
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp Support',
      desc: 'Get help anytime on WhatsApp',
      color: 'from-orange-400 to-pink-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
      {cards.map((card, i) => (
        <div
          key={i}
          className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-8 flex flex-col items-center text-center gap-4 transition-all duration-300 hover:-translate-y-1"
          style={{ animationDelay: `${i * 100}ms` }}
        >
          <div
            className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-r ${card.color}`}
          >
            <i className={`${card.icon} text-2xl text-white`} />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-base mb-1">{card.title}</h3>
            <p className="text-gray-500 text-sm">{card.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TrackOrderPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    document.title = 'Track Your Order - AiShopio Dubai UAE';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Track your AiShopio Dubai order in real-time. Enter your order number (AIS-XXXXXXXX) to get live delivery status updates across all UAE Emirates.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Track Your Order - AiShopio Dubai',
      description: 'Track your AiShopio Dubai order in real-time across all UAE Emirates.',
      url: `${siteUrl}/track-order`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Track Order', item: `${siteUrl}/track-order` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="track-order"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'track-order');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="track-order"]')?.remove(); };
  }, []);

  const handleTrack = async () => {
    const trimmed = orderNumber.trim().toUpperCase();
    if (!trimmed) return;

    setLoading(true);
    setNotFound(false);
    setOrder(null);
    setSearched(false);

    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', trimmed)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
      } else {
        setOrder(data as Order);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleTrack();
  };

  const isCancelled = order?.status === 'cancelled';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 via-white to-pink-50">
      <Header />

      <main className="flex-1 pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">

          {/* Page Title */}
          <div className="text-center mb-10 animate-fadeInUp">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-map-pin-time-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-xs font-semibold tracking-wide uppercase">Order Tracking</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-3">
              <span className="bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                Track Your Order
              </span>
            </h1>
            <p className="text-gray-500 text-base">
              Enter your order number to get real-time delivery updates
            </p>
          </div>

          {/* Search Box */}
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 md:p-8 mb-8 animate-fadeInUp">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-lg" />
                </div>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter your order number e.g. AIS-12345678"
                  className="w-full pl-11 pr-4 py-4 rounded-2xl border border-gray-200 bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent transition-all duration-200 placeholder-gray-400"
                />
              </div>
              <button
                onClick={handleTrack}
                disabled={loading || !orderNumber.trim()}
                className="whitespace-nowrap px-8 py-4 rounded-2xl font-bold text-white text-sm transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Tracking...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <i className="ri-map-pin-time-line" />
                    Track Order
                  </span>
                )}
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-3 text-center">
              <i className="ri-information-line mr-1" />
              Enter the order number you received on WhatsApp or email
            </p>
          </div>

          {/* Results */}
          {!loading && searched && order && !isCancelled && (
            <div className="space-y-6 animate-fadeInUp">

              {/* Order Status Card */}
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-1">Order Number</p>
                    <h2 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                      {order.order_number}
                    </h2>
                  </div>
                  <div className="flex flex-col items-start md:items-end gap-1">
                    <span
                      className="px-4 py-1.5 rounded-full text-xs font-bold text-white capitalize"
                      style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}
                    >
                      {order.status.replace(/_/g, ' ')}
                    </span>
                    <p className="text-gray-400 text-xs">{formatDate(order.created_at)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Customer', value: order.full_name, icon: 'ri-user-line' },
                    { label: 'Order Date', value: formatDate(order.created_at), icon: 'ri-calendar-line' },
                    { label: 'Payment', value: formatPayment(order.payment_method), icon: 'ri-bank-card-line' },
                    { label: 'Total', value: `AED ${Number(order.total).toFixed(2)}`, icon: 'ri-price-tag-3-line' },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50/80 rounded-2xl p-4">
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className={`${item.icon} text-orange-400 text-xs`} />
                        <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-800 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Progress Tracker */}
                <div className="py-4">
                  <ProgressTracker status={order.status} />
                </div>
              </div>

              {/* Delivery Details Card */}
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 md:p-8">
                <h3 className="font-bold text-gray-800 text-lg mb-5 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600">
                    <i className="ri-map-pin-line text-white text-sm" />
                  </div>
                  Delivery Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Delivery Address</p>
                      <p className="text-sm text-gray-700 font-medium">
                        {[order.building, order.apartment, order.area, order.emirate]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Delivery Type</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                        <i className="ri-truck-line text-xs" />
                        {order.delivery_type === 'express' ? 'Express Delivery' : 'Standard Delivery'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Estimated Delivery</p>
                      <p className="text-sm text-gray-700 font-medium">
                        {order.estimated_delivery || '3–5 business days'}
                      </p>
                    </div>
                    {order.notes && (
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Notes</p>
                        <p className="text-sm text-gray-600 italic">{order.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Need Help Section */}
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 md:p-8">
                <h3 className="font-bold text-gray-800 text-lg mb-2">Need help with your order?</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Our support team is available 24/7 to assist you with any questions.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://wa.me/971500000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-white text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                    style={{ background: '#25d366' }}
                  >
                    <i className="ri-whatsapp-line text-lg" />
                    Chat on WhatsApp
                  </a>
                  <a
                    href="mailto:hello@aishopio.ae"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-white text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}
                  >
                    <i className="ri-mail-send-line text-lg" />
                    Email Support
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Order */}
          {!loading && searched && order && isCancelled && (
            <div className="animate-fadeInUp">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-red-100 p-8 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
                  <i className="ri-close-circle-line text-3xl text-red-500" />
                </div>
                <h2 className="text-xl font-black text-gray-800 mb-2">Order Cancelled</h2>
                <p className="text-gray-500 text-sm mb-2">
                  Order <span className="font-bold text-red-500">{order.order_number}</span> has been cancelled.
                </p>
                <p className="text-gray-400 text-xs mb-6">
                  If you have any questions, please contact our support team.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="https://wa.me/971500000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold text-white text-sm cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
                    style={{ background: '#25d366' }}
                  >
                    <i className="ri-whatsapp-line" />
                    Contact on WhatsApp
                  </a>
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold text-white text-sm cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}
                  >
                    <i className="ri-store-2-line" />
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Not Found */}
          {!loading && searched && notFound && (
            <div className="animate-fadeInUp">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-10 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-orange-50 mx-auto mb-5">
                  <i className="ri-search-line text-4xl text-orange-400" />
                </div>
                <h2 className="text-2xl font-black text-gray-800 mb-3">Order Not Found</h2>
                <p className="text-gray-500 text-sm mb-1">
                  Please check your order number and try again.
                </p>
                <p className="text-gray-400 text-xs mb-8">
                  Order numbers start with <span className="font-bold text-orange-500">AIS-</span>
                </p>
                <a
                  href="https://wa.me/971500000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl font-bold text-white text-sm cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
                  style={{ background: '#25d366' }}
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Contact us on WhatsApp
                </a>
              </div>
            </div>
          )}

          {/* Empty State (before searching) */}
          {!searched && !loading && <FeatureCards />}
        </div>
      </main>

      <Footer />
    </div>
  );
}
